import { Card, CardContent, CardHeader, CardTitle } from "@exhousing/design-system";
import { Button } from "@exhousing/design-system";
import { Crown, Scale } from "lucide-react";
import { useState } from "react";

import { ScoreMeter } from "@/components/application/ScoreMeter";
import { SelectModal } from "@/components/application/SelectModal";
import { StatusBadge } from "@/components/StatusBadge";
import type { components } from "@/types/api";

type Row = components["schemas"]["ApplicationListItem"];

const FINALIST_STATUSES = new Set(["approved", "reserve", "under_option"]);

/** Journey 7.1: side-by-side view of the approved finalists for one property. */
export function ComparisonTable({ applications }: { applications: Row[] }) {
  const [selecting, setSelecting] = useState<Row | null>(null);
  const finalists = applications
    .filter((a) => FINALIST_STATUSES.has(a.status))
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  if (finalists.length === 0) return null;
  const optionOut = finalists.some((a) => a.status === "under_option");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Scale className="size-4" /> Final selection
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-6 py-3 font-medium">Candidate</th>
              <th className="px-6 py-3 font-medium">Score</th>
              <th className="px-6 py-3 font-medium">Income</th>
              <th className="px-6 py-3 font-medium">Rent quote</th>
              <th className="px-6 py-3 font-medium">Move-in</th>
              <th className="px-6 py-3 font-medium">Status</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody>
            {finalists.map((a) => (
              <tr key={a.id} className="border-b border-border/60 last:border-0">
                <td className="px-6 py-3 font-medium">{a.name}</td>
                <td className="px-6 py-3">
                  <ScoreMeter score={a.score} />
                </td>
                <td className="px-6 py-3 tabular-nums">
                  {a.gross_monthly_income != null
                    ? `€ ${Number(a.gross_monthly_income).toLocaleString("nl-NL")}`
                    : "—"}
                  {a.income_type ? (
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({a.income_type.replaceAll("_", " ")})
                    </span>
                  ) : null}
                </td>
                <td className="px-6 py-3 tabular-nums">
                  {a.affordability_pct != null ? `${a.affordability_pct}%` : "—"}
                </td>
                <td className="px-6 py-3">{a.desired_start_date ?? "—"}</td>
                <td className="px-6 py-3">
                  <StatusBadge status={a.status} />
                </td>
                <td className="px-6 py-3 text-right">
                  {!optionOut && a.status !== "under_option" && (
                    <Button size="sm" onClick={() => setSelecting(a)}>
                      <Crown className="size-4" /> Select
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      {selecting && (
        <SelectModal
          applicationId={selecting.id}
          candidateName={selecting.name}
          onClose={() => setSelecting(null)}
        />
      )}
    </Card>
  );
}
